import {
  AdminApiError,
  type AdminClient,
  type IntegratorKpiRow,
  type IntegratorSummary,
  type SupportedCurrencyView,
} from '@apx/admin-sdk';
import { emptyState } from '../empty-state.js';
import { fmtDate } from '../format.js';
import { clearFieldErrors, setFieldError } from '../form-validation.js';
import { confirmModal, promptModal } from '../modal.js';
import { skeletonTable } from '../skeleton.js';
import { showToast } from '../toast.js';

export interface IntegratorsPageDeps {
  client: AdminClient;
}

export async function renderIntegratorsPage(root: HTMLElement, deps: IntegratorsPageDeps): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Integrators</h1>
    <p class="page-sub">Multi-integrator control plane. Create, edit, suspend integrators. Per-game config + players nest under the selected integrator.</p>

    <details class="card" id="t-create-card">
      <summary class="t-create-summary">
        <h2 style="margin:0">Create new integrator</h2>
        <span class="muted" style="font-size:12px">Click to expand the onboarding form.</span>
      </summary>

      <div class="t-create-section">
        <h3>Identity</h3>
        <div class="form-grid">
          <label>Slug (lowercase, hyphenated)<input id="t-slug" type="text" placeholder="acme-corp" /></label>
          <label>Display name<input id="t-name" type="text" placeholder="ACME Corp" /></label>
          <label>Default locale<input id="t-locale" type="text" value="id" /></label>
        </div>
        <div class="t-currency-picker" style="margin-top:14px">
          <div class="row" style="justify-content:space-between;align-items:baseline;flex-wrap:wrap;gap:6px">
            <strong style="font-size:13px">Currencies</strong>
            <span class="muted" style="font-size:11px">Tick every currency this integrator trades in, then mark one as the player default. Codes come from the platform <a href="/currencies">registry</a>.</span>
          </div>
          <div id="t-currency-list" class="t-currency-list" style="margin-top:8px"><p class="muted" style="font-size:12px">Loading currencies…</p></div>
        </div>
      </div>

      <div class="t-create-section">
        <h3>Security &amp; access</h3>
        <p class="muted t-section-hint">All optional — leave blank to skip and edit later on the Integrator settings page. <strong>Empty allowedIps = open to any source IP (dev only).</strong></p>
        <div class="form-grid">
          <label style="grid-column:1 / span 2">allowedIps (CIDR list, one per line)
            <textarea id="t-allowed-ips" rows="3" placeholder="203.0.113.0/24&#10;2001:db8::/32"></textarea>
          </label>
          <label style="grid-column:1 / span 2">allowedOrigins (one URL per line, for iframe parent allowlist)
            <textarea id="t-allowed-origins" rows="3" placeholder="https://app.acme.example&#10;https://staging.acme.example"></textarea>
          </label>
        </div>
        <p class="muted t-section-hint" style="margin-top:6px">Enabled fiat currencies + per-fiat credit rates are managed under the integrator detail page after create.</p>
      </div>

      <div class="t-create-section">
        <h3>Wallet callback (HMAC)</h3>
        <p class="muted t-section-hint">apx calls the integrator outbound for balance/debit/credit. Set <code>walletBaseUrl</code> to enable — a fresh <code>walletAuthSecret</code> is generated and revealed once on success. Leave URL blank to skip wiring now.</p>
        <div class="form-grid">
          <label style="grid-column:1 / span 2">walletBaseUrl
            <input id="t-wallet-url" type="url" placeholder="https://api.integrator.example/apx" />
          </label>
          <label>walletTimeoutMs
            <input id="t-wallet-timeout" type="number" min="100" max="10000" value="3000" />
          </label>
          <label>walletDriftTolerance
            <input id="t-drift-tolerance" type="number" min="0" value="0" />
          </label>
        </div>
      </div>

      <div class="t-create-section">
        <h3>First admin</h3>
        <p class="muted t-section-hint">Creates the integrator's own login, granted the full <strong>Integrator Admin</strong> capability set — including the ability to add their own staff (finance, support) from their Staff page. Leave blank to create the integrator without a login; provider staff can administer it and you can add an admin later from Staff.</p>
        <div class="form-grid">
          <label style="grid-column:1 / span 2">Email
            <input id="t-admin-email" type="email" placeholder="admin@integrator.example" autocomplete="off" />
          </label>
          <label style="grid-column:1 / span 2">Password (min 8 chars)
            <input id="t-admin-password" type="password" autocomplete="new-password" />
          </label>
        </div>
        <p class="muted t-section-hint" style="margin-top:6px">Hand this over the same way as the starter API key — apx does not email it.</p>
      </div>

      <div class="row" style="padding:0 18px 18px">
        <button id="t-create" class="primary">Create integrator</button>
        <button id="t-create-reset" class="ghost" type="button">Reset form</button>
        <span id="t-msg" class="muted"></span>
      </div>
    </details>

    <div class="card">
      <div class="row" style="justify-content:space-between;align-items:center;flex-wrap:wrap;gap:10px">
        <h2 style="margin:0">Existing integrators</h2>
        <div id="t-filter" class="seg" role="tablist" aria-label="Filter integrators by status">
          <button type="button" class="seg-btn" data-filter="active" role="tab" aria-selected="true">Active <span class="seg-count" data-count="active">0</span></button>
          <button type="button" class="seg-btn" data-filter="suspended" role="tab" aria-selected="false">Suspended <span class="seg-count" data-count="suspended">0</span></button>
          <button type="button" class="seg-btn" data-filter="all" role="tab" aria-selected="false">All <span class="seg-count" data-count="all">0</span></button>
        </div>
      </div>
      <div id="t-list">${skeletonTable({ rows: 6, columns: 7 })}</div>
    </div>
  `;

  const msgEl = root.querySelector<HTMLElement>('#t-msg')!;
  const listEl = root.querySelector<HTMLElement>('#t-list')!;
  const filterEl = root.querySelector<HTMLElement>('#t-filter')!;

  // Default view hides suspended integrators. 'all' is opt-in via the filter.
  type StatusFilter = 'active' | 'suspended' | 'all';
  let statusFilter: StatusFilter = 'active';
  let allRows: IntegratorKpiRow[] = [];

  function applyFilter(): void {
    const counts = {
      active: allRows.filter((t) => t.status === 'active').length,
      suspended: allRows.filter((t) => t.status === 'suspended').length,
      all: allRows.length,
    };
    filterEl.querySelectorAll<HTMLElement>('.seg-count').forEach((el) => {
      const key = el.dataset.count as StatusFilter;
      el.textContent = String(counts[key]);
    });
    filterEl.querySelectorAll<HTMLButtonElement>('.seg-btn').forEach((btn) => {
      const active = btn.dataset.filter === statusFilter;
      btn.classList.toggle('active', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    const visible =
      statusFilter === 'all' ? allRows : allRows.filter((t) => t.status === statusFilter);
    listEl.innerHTML = renderTable(visible, statusFilter);
    bindEditButtons();
  }

  filterEl.querySelectorAll<HTMLButtonElement>('.seg-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      statusFilter = (btn.dataset.filter as StatusFilter) ?? 'active';
      applyFilter();
    });
  });

  // ─────────── Currency picker (create form). Registry-driven: an integrator
  // can trade in multiple currencies, one of which is the player default. Each
  // ticked currency becomes an enabled IntegratorFiatCurrency row at create; a
  // "default" radio is selectable only for ticked currencies.
  const currencyListEl = root.querySelector<HTMLElement>('#t-currency-list')!;
  let loadedCurrencies: SupportedCurrencyView[] = [];

  function renderCurrencyPicker(): void {
    if (loadedCurrencies.length === 0) {
      currencyListEl.innerHTML = `<p class="muted" style="font-size:12px">No platform currencies are enabled. Add one on the <a href="/currencies">Currencies</a> page before creating an integrator.</p>`;
      return;
    }
    const rows = loadedCurrencies
      .map((c, i) => {
        const label = `${escapeHtml(c.displayName)}${c.symbol ? ` (${escapeHtml(c.symbol)})` : ''}`;
        const first = i === 0;
        return `<tr>
          <td style="padding:4px 10px 4px 0"><input type="checkbox" data-cur-enable="${escapeHtml(c.code)}"${first ? ' checked' : ''} /></td>
          <td style="padding:4px 10px 4px 0"><input type="radio" name="t-cur-default" data-cur-default="${escapeHtml(c.code)}"${first ? ' checked' : ' disabled'} /></td>
          <td style="padding:4px 0"><code>${escapeHtml(c.code)}</code> <span class="muted" style="font-size:11px">${label}</span></td>
        </tr>`;
      })
      .join('');
    currencyListEl.innerHTML = `<table class="t-currency-table" style="border-collapse:collapse">
      <thead><tr>
        <th style="text-align:left;font-size:11px;font-weight:600;color:var(--muted);padding:0 10px 4px 0">Enable</th>
        <th style="text-align:left;font-size:11px;font-weight:600;color:var(--muted);padding:0 10px 4px 0">Default</th>
        <th style="text-align:left;font-size:11px;font-weight:600;color:var(--muted);padding:0 0 4px">Currency</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>`;

    // A currency can only be the default while it is enabled. Unticking the
    // current default reassigns it to the first still-ticked currency.
    currencyListEl.querySelectorAll<HTMLInputElement>('input[data-cur-enable]').forEach((cb) => {
      cb.addEventListener('change', () => {
        const code = cb.dataset.curEnable!;
        const radio = currencyListEl.querySelector<HTMLInputElement>(
          `input[data-cur-default="${CSS.escape(code)}"]`,
        );
        if (!radio) return;
        radio.disabled = !cb.checked;
        if (!cb.checked && radio.checked) {
          radio.checked = false;
          const firstEnabled = currencyListEl.querySelector<HTMLInputElement>(
            'input[data-cur-enable]:checked',
          );
          if (firstEnabled) {
            const nextDefault = currencyListEl.querySelector<HTMLInputElement>(
              `input[data-cur-default="${CSS.escape(firstEnabled.dataset.curEnable!)}"]`,
            );
            if (nextDefault) nextDefault.checked = true;
          }
        }
      });
    });
  }

  /** Enabled codes + the chosen default, read live from the picker DOM. */
  function currencySelection(): { codes: string[]; defaultCode: string | null } {
    const codes = Array.from(
      currencyListEl.querySelectorAll<HTMLInputElement>('input[data-cur-enable]:checked'),
    ).map((cb) => cb.dataset.curEnable!);
    const defaultRadio = currencyListEl.querySelector<HTMLInputElement>(
      'input[data-cur-default]:checked',
    );
    const defaultCode =
      defaultRadio && !defaultRadio.disabled ? (defaultRadio.dataset.curDefault ?? null) : null;
    return { codes, defaultCode };
  }

  async function loadCurrencies(): Promise<void> {
    try {
      const currencies = await deps.client.listCurrencies();
      loadedCurrencies = currencies
        .filter((c) => c.enabled)
        .sort((a, b) => a.sortOrder - b.sortOrder);
      renderCurrencyPicker();
    } catch (err) {
      currencyListEl.innerHTML = `<p class="error" style="font-size:12px">${escapeHtml(describe(err))}</p>`;
    }
  }

  async function refresh(): Promise<void> {
    try {
      // Prefer summary endpoint — it carries name/slug/status + 24h KPIs in one
      // call. Fall back to plain list if summary 404s (older backend).
      let summary: IntegratorKpiRow[] = [];
      try {
        summary = await deps.client.listIntegratorsSummary();
      } catch {
        const basic = await deps.client.listIntegrators();
        summary = basic
          .filter((t) => !t.isProvider) // hide the reserved provider-default tenant
          .map((t) => ({
          id: t.id,
          slug: t.slug,
          name: t.name,
          status: t.status,
          defaultFiatCurrency: t.defaultFiatCurrency,
          createdAt: t.createdAt,
          kpi24h: { turnover: '0', payout: '0', ggr: '0', rtp: 0, betCount: 0, rounds: 0, players: 0 },
        }));
      }
      allRows = summary;
      applyFilter();
    } catch (err) {
      listEl.innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
    }
  }

  function renderTable(rows: IntegratorKpiRow[], filter: StatusFilter): string {
    if (rows.length === 0) {
      if (filter === 'suspended') {
        return emptyState({
          icon: 'list',
          title: 'No suspended integrators',
          hint: 'Suspended integrators show up here. Switch to Active or All to see the rest.',
        });
      }
      if (filter === 'active' && allRows.length > 0) {
        return emptyState({
          icon: 'list',
          title: 'No active integrators',
          hint: 'Every integrator is currently suspended. Switch to Suspended or All to see them.',
        });
      }
      return emptyState({
        icon: 'list',
        title: 'No integrators yet',
        hint: 'Create one above to start onboarding an integrator.',
      });
    }
    return `<table>
      <thead><tr>
        <th>Integrator</th>
        <th>Currency</th>
        <th>Status</th>
        <th style="text-align:right">Turnover 24h</th>
        <th style="text-align:right">GGR 24h</th>
        <th style="text-align:right">Rounds</th>
        <th style="text-align:right">Players</th>
        <th>Created</th>
        <th></th>
      </tr></thead>
      <tbody>${rows
        .map(
          (t) => `<tr>
            <td>
              <a href="/integrators/${t.id}/home" style="color:var(--accent-2);text-decoration:none"><strong>${escapeHtml(t.name)}</strong></a>
              <div class="muted" style="font-size:11px"><code>${t.slug}</code></div>
            </td>
            <td><code>${escapeHtml(t.defaultFiatCurrency)}</code></td>
            <td><span class="pill ${t.status === 'active' ? 'live' : 'warn'}">${t.status}</span></td>
            <td style="text-align:right;font-variant-numeric:tabular-nums">${fmtBig(t.kpi24h.turnover)}</td>
            <td style="text-align:right;font-variant-numeric:tabular-nums">${fmtBig(t.kpi24h.ggr)}</td>
            <td style="text-align:right;font-variant-numeric:tabular-nums">${t.kpi24h.rounds.toLocaleString()}</td>
            <td style="text-align:right;font-variant-numeric:tabular-nums">${t.kpi24h.players.toLocaleString()}</td>
            <td><span class="muted">${fmtDate(t.createdAt)}</span></td>
            <td>
              <button data-rename="${t.id}" data-current-name="${escapeHtml(t.name)}">Rename</button>
              <button data-toggle-status="${t.id}" data-current="${t.status}" class="ghost">${t.status === 'active' ? 'Suspend' : 'Reactivate'}</button>
            </td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
  }

  function fmtBig(s: string): string {
    try {
      return BigInt(s).toLocaleString();
    } catch {
      return s;
    }
  }

  function bindEditButtons(): void {
    listEl.querySelectorAll<HTMLButtonElement>('button[data-rename]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset.rename!;
        const next = await promptModal({
          title: 'Rename integrator',
          body: 'New display name shown across the admin UI + integrator listings.',
          defaultValue: btn.dataset.currentName ?? '',
          confirmLabel: 'Save',
        });
        if (!next) return;
        try {
          await deps.client.updateIntegrator(id, { name: next });
          await refresh();
        } catch (err) {
          showToast(describe(err), 'error');
        }
      });
    });
    listEl.querySelectorAll<HTMLButtonElement>('button[data-toggle-status]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset.toggleStatus!;
        const current = btn.dataset.current ?? 'active';
        const next = current === 'active' ? 'suspended' : 'active';
        if (next === 'suspended') {
          const ok = await confirmModal({
            title: 'Suspend this integrator?',
            body: 'New sessions will be rejected until reactivated. Active rounds finish normally.',
            confirmLabel: 'Suspend',
            danger: true,
          });
          if (!ok) return;
        }
        try {
          await deps.client.updateIntegrator(id, { status: next });
          await refresh();
        } catch (err) {
          showToast(describe(err), 'error');
        }
      });
    });
  }

  function splitLines(raw: string): string[] {
    return raw
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }

  function splitCsv(raw: string): string[] {
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }

  function resetForm(): void {
    clearFieldErrors(root);
    msgEl.textContent = '';
    root.querySelector<HTMLInputElement>('#t-slug')!.value = '';
    root.querySelector<HTMLInputElement>('#t-name')!.value = '';
    root.querySelector<HTMLInputElement>('#t-locale')!.value = 'id';
    renderCurrencyPicker();
    root.querySelector<HTMLTextAreaElement>('#t-allowed-ips')!.value = '';
    root.querySelector<HTMLTextAreaElement>('#t-allowed-origins')!.value = '';
    root.querySelector<HTMLInputElement>('#t-wallet-url')!.value = '';
    root.querySelector<HTMLInputElement>('#t-wallet-timeout')!.value = '3000';
    root.querySelector<HTMLInputElement>('#t-drift-tolerance')!.value = '0';
    // Never leave a password sitting in the DOM for the next create.
    root.querySelector<HTMLInputElement>('#t-admin-email')!.value = '';
    root.querySelector<HTMLInputElement>('#t-admin-password')!.value = '';
  }

  root.querySelector<HTMLButtonElement>('#t-create-reset')?.addEventListener('click', () => resetForm());

  root.querySelector<HTMLButtonElement>('#t-create')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    clearFieldErrors(root);
    const slugEl = root.querySelector<HTMLInputElement>('#t-slug')!;
    const nameEl = root.querySelector<HTMLInputElement>('#t-name')!;
    const slug = (slugEl.value || '').trim();
    const name = (nameEl.value || '').trim();
    const defaultLocale = (root.querySelector<HTMLInputElement>('#t-locale')!.value || 'id').trim();
    const { codes: fiatCurrencies, defaultCode: defaultFiatCurrency } = currencySelection();
    const allowedIps = splitLines(root.querySelector<HTMLTextAreaElement>('#t-allowed-ips')!.value);
    const allowedOrigins = splitLines(root.querySelector<HTMLTextAreaElement>('#t-allowed-origins')!.value);
    const walletBaseUrl = root.querySelector<HTMLInputElement>('#t-wallet-url')!.value.trim();
    const walletTimeoutMs = Number(root.querySelector<HTMLInputElement>('#t-wallet-timeout')!.value);
    const driftRaw = root.querySelector<HTMLInputElement>('#t-drift-tolerance')!.value.trim();

    let bad = false;
    if (!slug) { setFieldError(slugEl, 'Slug required'); bad = true; }
    else if (!/^[a-z0-9-]+$/.test(slug)) { setFieldError(slugEl, 'Lowercase alphanumeric + hyphen only'); bad = true; }
    if (!name) { setFieldError(nameEl, 'Display name required'); bad = true; }
    if (walletBaseUrl && !/^https?:\/\//.test(walletBaseUrl)) {
      setFieldError(root.querySelector<HTMLInputElement>('#t-wallet-url')!, 'Must start with http(s)://');
      bad = true;
    }
    if (fiatCurrencies.length === 0 || !defaultFiatCurrency) {
      msgEl.textContent = 'Tick at least one currency and mark one as the default.';
      msgEl.className = 'error';
      bad = true;
    }
    // Both admin fields or neither — a half-filled pair means the operator
    // intended a login and would otherwise get an integrator without one.
    const adminEmailEl = root.querySelector<HTMLInputElement>('#t-admin-email')!;
    const adminPasswordEl = root.querySelector<HTMLInputElement>('#t-admin-password')!;
    const adminEmail = adminEmailEl.value.trim();
    const adminPassword = adminPasswordEl.value;
    if (adminEmail && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(adminEmail)) {
      setFieldError(adminEmailEl, 'Enter a valid email');
      bad = true;
    }
    if (adminEmail && adminPassword.length < 8) {
      setFieldError(adminPasswordEl, 'At least 8 characters');
      bad = true;
    }
    if (!adminEmail && adminPassword) {
      setFieldError(adminEmailEl, 'Email required to create the first admin');
      bad = true;
    }
    if (bad) return;
    // Narrowed by the currency validation above.
    if (!defaultFiatCurrency) return;

    msgEl.textContent = 'Creating…';
    msgEl.className = 'muted';

    try {
      const created = await deps.client.createIntegrator({
        slug,
        name,
        defaultLocale,
        defaultFiatCurrency,
        fiatCurrencies,
        ...(allowedIps.length ? { allowedIps } : {}),
        ...(allowedOrigins.length ? { allowedOrigins } : {}),
        ...(walletBaseUrl ? { walletBaseUrl } : {}),
        ...(Number.isFinite(walletTimeoutMs) ? { walletTimeoutMs } : {}),
        ...(driftRaw ? { walletDriftTolerance: driftRaw } : {}),
        ...(adminEmail ? { admin: { email: adminEmail, password: adminPassword } } : {}),
      });
      msgEl.textContent = created.admin
        ? `Created ${created.slug} (${created.id.slice(0, 8)}…) with admin ${created.admin.email}.`
        : `Created ${created.slug} (${created.id.slice(0, 8)}…).`;
      msgEl.className = 'muted';
      showStarterSecretsModal({
        apiKey: created.apiKey.plaintextKey,
        apiKeyWarning: created.apiKey.warning,
        walletSecret: created.walletAuthSecret,
        walletSecretWarning: created.walletAuthSecretWarning,
        adminEmail: created.admin?.email ?? null,
      });
      resetForm();
      // Collapse the form so the new row is the focus.
      const card = root.querySelector<HTMLDetailsElement>('#t-create-card');
      if (card) card.open = false;
      await refresh();
    } catch (err) {
      msgEl.textContent = describe(err);
      msgEl.className = 'error';
    }
  });

  await Promise.all([refresh(), loadCurrencies()]);
}

/**
 * Reveal modal for credentials minted at integrator creation. Always shows
 * the starter API key. Optionally shows the wallet auth secret when the
 * operator wired walletBaseUrl at create time. Both are plaintext and shown
 * exactly once — backend only persists hashes.
 */
function showStarterSecretsModal(args: {
  apiKey: string;
  apiKeyWarning: string;
  walletSecret: string | null;
  walletSecretWarning: string | null;
  adminEmail?: string | null;
}): void {
  const backdrop = document.createElement('div');
  backdrop.className = 'modal-backdrop';
  // The password is not echoed — the operator chose it. Naming the account
  // confirms it exists and says who to send it to.
  const adminBlock = args.adminEmail
    ? `
      <div class="starter-secret-block">
        <div class="starter-secret-label">First admin</div>
        <code class="starter-secret-value">${escapeHtml(args.adminEmail)}</code>
        <p class="muted starter-secret-hint">Can sign in now with the password you set, and add their own staff from the Staff page. Send both over your usual channel — apx does not email credentials.</p>
      </div>`
    : '';
  const walletBlock = args.walletSecret
    ? `
      <div class="starter-secret-block">
        <div class="starter-secret-label">walletAuthSecret</div>
        <code class="starter-secret-value" data-secret="wallet">${escapeHtml(args.walletSecret)}</code>
        <p class="muted starter-secret-hint">${escapeHtml(args.walletSecretWarning ?? 'Shown once.')}</p>
        <button type="button" class="ghost starter-secret-copy" data-target="wallet">Copy</button>
      </div>`
    : '';
  backdrop.innerHTML = `
    <div role="dialog" class="modal-card" style="width:min(620px,94vw)">
      <h2 class="modal-title">Integrator created — copy credentials now</h2>
      <p class="modal-body">These are shown <strong>once</strong>. apx only persists their SHA-256 hashes. If lost, rotate via the integrator settings page.</p>

      <div class="starter-secret-block">
        <div class="starter-secret-label">API key (Authorization: ApiKey &lt;…&gt;)</div>
        <code class="starter-secret-value" data-secret="apikey">${escapeHtml(args.apiKey)}</code>
        <p class="muted starter-secret-hint">${escapeHtml(args.apiKeyWarning)}</p>
        <button type="button" class="ghost starter-secret-copy" data-target="apikey">Copy</button>
      </div>

      ${walletBlock}
      ${adminBlock}

      <label style="display:flex;align-items:center;gap:8px;margin:18px 0 0;font-size:13px">
        <input id="t-secret-ack" type="checkbox" /> I have copied ${args.walletSecret ? 'both credentials' : 'the API key'} somewhere safe.
      </label>
      <div class="modal-actions">
        <button id="t-secret-close" class="primary" disabled>Close</button>
      </div>
    </div>
  `;
  document.body.appendChild(backdrop);

  const ack = backdrop.querySelector<HTMLInputElement>('#t-secret-ack')!;
  const closeBtn = backdrop.querySelector<HTMLButtonElement>('#t-secret-close')!;
  ack.addEventListener('change', () => {
    closeBtn.disabled = !ack.checked;
  });
  backdrop.querySelectorAll<HTMLButtonElement>('.starter-secret-copy').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const target = btn.dataset.target!;
      const codeEl = backdrop.querySelector<HTMLElement>(`code[data-secret="${target}"]`);
      const value = codeEl?.textContent ?? '';
      try {
        await navigator.clipboard.writeText(value);
        btn.textContent = 'Copied!';
        setTimeout(() => (btn.textContent = 'Copy'), 1500);
      } catch {
        /* clipboard denied — user can select manually */
      }
    });
  });
  closeBtn.addEventListener('click', () => backdrop.remove());
}

function describe(err: unknown): string {
  if (err instanceof AdminApiError) return `${err.code}: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}
